/**
 * Member-facing wastewater methodology copy.
 * Centers the 2020 ClimatePlans wastewater inventory (UWMP volumes + ICLEI/IPCC
 * process factors). Other-year scaling is a short follow-on, not the method.
 */

export interface WastewaterMethodologyActivity {
  facilityName: string | null;
  treatmentVolumeAf: number | null;
  treatmentLevel: string | null;
  disposalMethod: string | null;
  inventoryYear: number | null;
  volumeMethod?: string | null;
}

export interface WastewaterMethodologyCopy {
  description: string;
  dataSources: string[];
  methodology: string[];
}

function formatAf(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function treatmentLabel(level: string | null | undefined): string | null {
  if (!level) return null;
  return level.replace(/_/g, ' ');
}

function n2oFactorForLevel(level: string | null | undefined): string | null {
  const key = (level || '').toLowerCase();
  if (key === 'tertiary' || key === 'advanced') {
    return '0.02568 MT CO₂e per acre-foot (tertiary / advanced; 10 mg N/L effluent nitrogen)';
  }
  if (key === 'secondary' || key === 'secondary_disinfected') {
    return '0.08989 MT CO₂e per acre-foot (secondary; 35 mg N/L effluent nitrogen)';
  }
  return null;
}

function volumeSourceSentence(activity: WastewaterMethodologyActivity | null | undefined): string | null {
  if (!activity) return null;
  const method = (activity.volumeMethod || '').toLowerCase();
  const year = activity.inventoryYear ?? 2020;
  const volume =
    activity.treatmentVolumeAf != null && Number.isFinite(activity.treatmentVolumeAf)
      ? `${formatAf(activity.treatmentVolumeAf)} acre-feet`
      : null;
  const facility = activity.facilityName ? `, associated with ${activity.facilityName}` : '';

  if (method === 'supplemental' || method === 'metered') {
    const how =
      method === 'metered'
        ? 'metered treatment-plant flow'
        : 'this city’s Urban Water Management Plan collection volume';
    if (volume) {
      return `For this city, the ${year} treatment volume is ${volume} from ${how}${facility}.`;
    }
    return `For this city, the ${year} treatment volume is taken from ${how}${facility}.`;
  }
  if (method === 'estimated' || method === 'scaled' || method === 'default') {
    const how =
      method === 'default'
        ? 'a statewide default generation rate (100 gallons per person-equivalent per day)'
        : 'service population × 100 gallons per person-equivalent per day (ICLEI USCP default)';
    if (volume) {
      return `For this city, the ${year} treatment volume is ${volume}, estimated as ${how}${facility}.`;
    }
    return `For this city, the ${year} treatment volume is estimated as ${how}${facility}.`;
  }
  if (volume) {
    return `For this city, the ${year} treatment volume is ${volume}${facility}.`;
  }
  return null;
}

export const WASTEWATER_2020_METHODOLOGY_DESCRIPTION =
  'The 2020 wastewater value is a ClimatePlans process-emissions inventory for municipal treatment. Emissions are attributed to the city whose residents and workers generate the wastewater, not necessarily to the city where the plant sits. California plants are mostly aerobic, so methane from treatment is typically zero and nitrous oxide from effluent nitrogen dominates. Plant electricity is counted in building energy, not here. Biosolids digestion and septic systems are not included.';

export const WASTEWATER_2020_DATA_SOURCES: string[] = [
  'California Urban Water Management Plans (UWMP 2020): wastewater collection volumes and reported treatment level, disposal method, and treated volume',
  'City–supplier–facility crosswalk from UWMP service areas, California Integrated Water Quality System (CIWQS) facility records, and EPA NPDES permits',
  'ICLEI U.S. Community Protocol (USCP) Appendix F: community-scale wastewater factors and the 100 gallons per person-equivalent per day default generation rate',
  'IPCC 2006 Guidelines for National Greenhouse Gas Inventories, Volume 5, Chapter 6: domestic wastewater CH₄ and N₂O equations',
  'IPCC AR5 100-year global warming potentials: CH₄ = 28, N₂O = 265',
  'Residential population and workplace employment (Census / LODES), used only to scale the 2020 inventory to other years',
];

export function wastewater2020MethodologySteps(
  activity?: WastewaterMethodologyActivity | null
): string[] {
  const year = activity?.inventoryYear ?? 2020;
  const label = treatmentLabel(activity?.treatmentLevel);
  const factor = n2oFactorForLevel(activity?.treatmentLevel);
  const cityVolume = volumeSourceSentence(activity);
  const cityTreatment = label
    ? `This city is classified as ${label} treatment in the ${year} inventory${factor ? `, so N₂O uses ${factor}` : ''}.`
    : null;

  const steps = [
    `Build a ${year} city → water supplier → treatment facility crosswalk so volume is attributed to the community that generates the wastewater.`,
    'Prefer UWMP-reported collection volume when a city has a clean single-supplier match. Otherwise estimate volume as service population × 100 gallons per person-equivalent per day (ICLEI USCP Appendix F default). Service population is residents plus workplace workers.',
    'Assign treatment level from the UWMP (tertiary, secondary-disinfected, secondary, or a statewide default when unreported).',
    'Calculate N₂O from effluent nitrogen using IPCC 2006 Volume 5, Chapter 6: volume × effluent nitrogen concentration (10 mg N/L for tertiary; 35 mg N/L for secondary) × 0.005 kg N₂O-N per kg N × GWP 265. That simplifies to 0.02568 MT CO₂e per acre-foot for tertiary treatment and 0.08989 MT CO₂e per acre-foot for secondary treatment. Land application or percolation uses a 0.5 attenuation factor.',
    'Calculate CH₄ from biological treatment using IPCC 2006. California municipal plants in this inventory are aerobic (methane correction factor 0.0), so treatment CH₄ is zero. Biosolids digestion and septic systems are excluded.',
  ];

  if (cityVolume) steps.push(cityVolume);
  if (cityTreatment) steps.push(cityTreatment);

  steps.push(
    'Other years are not independent inventories. They scale the 2020 estimate by the change in service population (residents plus workplace workers), or by resident population if workers are missing. Missing wastewater is not assigned zero. On the GHG inventory page, this estimate is added to landfill waste as Waste & wastewater.'
  );

  return steps;
}

export function wastewaterMethodologyCopy(
  activity?: WastewaterMethodologyActivity | null
): WastewaterMethodologyCopy {
  return {
    description: WASTEWATER_2020_METHODOLOGY_DESCRIPTION,
    dataSources: WASTEWATER_2020_DATA_SOURCES,
    methodology: wastewater2020MethodologySteps(activity),
  };
}

/** Compact GHG-page bullet: 2020 method first, scaling second. */
export const GHG_PAGE_WASTEWATER_METHODOLOGY =
  'Wastewater (2020 process emissions): City-attributed UWMP 2020 treatment volume × ICLEI USCP Appendix F / IPCC 2006 N₂O effluent factors (0.02568 MT CO₂e/AF tertiary; 0.08989 MT CO₂e/AF secondary). California aerobic plants have methane correction factor 0.0, so treatment CH₄ is zero. Plant electricity is in building energy. Biosolids and septic are excluded. Other years scale the 2020 inventory by service population (residents plus workplace workers), or by population if workers are missing; missing wastewater is not assigned zero.';

export { GHG_PAGE_WASTE_METHODOLOGY_DETAILED as GHG_PAGE_WASTE_METHODOLOGY } from './wasteMemberCopy';
